import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';

import { searchApi as api } from 'api/search';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

function ExpansionDetailComponent({ facilityID, parentRow, setExpandedRow }) {
    const [details, setDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [forceCacheAttempted, setForceCacheAttempted] = useState(false);

    useEffect(() => {
        const conditionMet = parentRow && parentRow.original && parentRow.original.name && facilityID;

        if (conditionMet) {
            const searchStr = parentRow.original.name;
            setIsLoading(true);
            setError(null);

            const apiOptions = {};
            if (forceCacheAttempted) {
                apiOptions.forceCacheIfNotFound = true;
            }

            api.checkLLMSearchResults(searchStr, facilityID, apiOptions)
                .then((res) => {
                    if (res && res.data) {
                        setDetails(res.data.rows || res.data);
                    } else {
                        setDetails([]);
                    }
                })
                .catch((err) => {
                    console.error('Error fetching expansion details:', err);
                    setError('Failed to load details.');
                    setDetails(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [parentRow, facilityID, forceCacheAttempted]);

    const handleForceCache = () => {
        setForceCacheAttempted(true);
    };

    if (isLoading) {
        return <div style={{ padding: '10px' }}>Loading details...</div>;
    }

    if (error) {
        return <div style={{ padding: '10px', color: 'red' }}>{error}</div>;
    }

    if (!isLoading && !error && (!details || details.length === 0) && !forceCacheAttempted) {
        return (
            <div style={{ padding: '10px' }}>
                No details found.
                <button
                    onClick={handleForceCache}
                    style={{
                        marginLeft: '10px',
                        color: 'white',
                        backgroundColor: 'red',
                        border: 'none',
                        padding: '5px 10px',
                        cursor: 'pointer',
                    }}
                >
                    Cache results
                </button>
            </div>
        );
    }

    if (!details || details.length === 0) {
        return <div style={{ padding: '10px' }}>No details found. (Cache attempt made)</div>;
    }

    return (
        <div style={{ padding: '10px', backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}>
            <h5 style={{ marginTop: 0, marginBottom: '5px' }}>Details:</h5>
            <ul>
                {details.map((item, index) => (
                    <li key={item.id || index}>
                        {item.name} (ID: {item.id})
                    </li>
                ))}
            </ul>
        </div>
    );
}

function LLMSearchManagement({}) {
    const [rows, setRows] = useState([]);
    const [newSearchString, setNewSearchString] = useState('');
    const [notification, setNotification] = useState({ text: '', level: '' });
    const user = useContext(UserContext);

    const COST_PER_ROW_WEEKLY = 0.45;

    const fetchCacheList = () => {
        api.getLLMCacheList().then((res) => {
            if (res && res.data && res.data.rows) {
                setRows(res.data.rows);
            } else {
                setRows([]);
            }
        });
    };

    useEffect(() => {
        fetchCacheList();
    }, []);

    const handleAddSearchString = async () => {
        if (newSearchString.trim() === '') {
            return;
        }
        try {
            await api.addSearchString(newSearchString.trim());
            setNewSearchString('');
            fetchCacheList();
            setNotification({ text: 'Successfully added to cache.', level: 'success' });
        } catch (error) {
            console.error('Failed to add search string:', error);
            setNotification({ text: 'Failed to add to cache.', level: 'error' });
        }
    };

    const handleRemoveSearchString = async (searchStr) => {
        try {
            await api.removeSearchString(searchStr);
            fetchCacheList();
            setNotification({ text: 'Successfully removed from cache.', level: 'success' });
        } catch (error) {
            console.error('Failed to remove search string:', error);
            setNotification({ text: 'Failed to remove from cache.', level: 'error' });
        }
    };

    const weeklyCost = (rows.length * COST_PER_ROW_WEEKLY).toFixed(2);
    const yearlyCost = (parseFloat(weeklyCost) * 52).toFixed(2);

    const isStringInCache = rows.some((row) => row.name === newSearchString.trim());

    const cols = [
        {
            accessor: 'name',
            Header: 'Name',
        },
        {
            Header: 'Actions',
            id: 'actions',
            Cell: ({ row }) => (
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSearchString(row.original.name);
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <>
            <Notifications options={notification} />
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    LLM Search Cache Management
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1">
                        Each row costs ${COST_PER_ROW_WEEKLY.toFixed(2)} per week to cache.
                    </Typography>
                    <Typography variant="body1">Current Weekly Cost: ${weeklyCost}</Typography>
                    <Typography variant="body1" gutterBottom>
                        Current Yearly Cost: ${yearlyCost}
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom>
                        Click a row to see current search results for the current pack group in this facility.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                        label="New Search String"
                        variant="outlined"
                        size="small"
                        value={newSearchString}
                        onChange={(e) => setNewSearchString(e.target.value)}
                        sx={{ mr: 1, flexGrow: 1 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddSearchString}
                        disabled={isStringInCache || newSearchString.trim() === ''}
                    >
                        Add to Cache
                    </Button>
                </Box>

                <DataTable
                    editable
                    columns={cols}
                    data={rows}
                    saveEdit={async ({ field, id, value }) => true}
                    expandable
                    ExpansionComponent={<ExpansionDetailComponent facilityID={user.getFacilityID()} />}
                />
            </Box>
        </>
    );
}

export { LLMSearchManagement };
